import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { MdClose, MdAdd, MdCloudUpload } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import MediaLibrary from "./MediaLibrary";
import MenuSelector from "./MenuSelector";
import type { RootState } from "../../redux/store";
import { fetchMenuItemsRequest } from "../../redux/slices/menuItemsSlice";
import { uploadImageRequest } from "../../redux/slices/imageUploadSlice";
import { toneMoodOptions, targetAudienceOptions, aspectRatio, shortCodeOptions} from "../../constants";

interface CreateOwnFormProps {
  onSubmit: (data: any) => void;
  initialData?: {
  eventDate?: string;
  title?: string;
  promotionType?: string;
  offerDetails?: string;
  Instructions?: string;
  tagLine?: string;
  validFrom?: string;
  validTo?: string;
  contactInfo?: string;
  refImage?: string[];
  themeColour?: string;
  menuItemNames?: string[];
  category?: string;
  flyerImage?: string;
  };
  showOnlyUploadSection?: boolean;
  isSubmitting?: boolean;
  submitLabel?: string;
  postYourOwn?: boolean;
}

interface PendingImage {
  id: string;
  file?: File;
  pexelsUrl?: string;
  templateUrl?: string;
  preview: string;
  type: "local" | "pexels" | "templates";
}

const CreateOwnForm: React.FC<CreateOwnFormProps> = ({
  onSubmit,
  initialData,
  showOnlyUploadSection = false,
  isSubmitting = false,
  submitLabel,
  postYourOwn,
}) => {
const dispatch = useDispatch();
const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

const { items: menuItems, loading: menuLoading } = useSelector((state: RootState) => state.menuItems,);

const [showMediaLibrary, setShowMediaLibrary] = useState(false);
const [showColorPicker, setShowColorPicker] = useState(false);
const [showAspectRatioPicker, setShowAspectRatioPicker] = useState(false);
const [selectedColor, setSelectedColor] = useState(initialData?.themeColour || "#EE4423",);
const [selectedMenuItems, setSelectedMenuItems] = useState<string[]>([]);
const [selectedToneMood, setSelectedToneMood] = useState("");
const [selectedTargetAudience, setSelectedTargetAudience] = useState("");
const [selectedshortCode, setSelectedshortCode] = useState("");
const [selectedAspectRatio, setSelectedAspectRatio] = useState("");

const [customWidth, setCustomWidth] = useState("896");
const [customHeight, setCustomHeight] = useState("1200");
const [isCustomSize, setIsCustomSize] = useState(false);

const colorPickerRef = useRef<HTMLDivElement>(null);
const aspectRatioPickerRef = useRef<HTMLDivElement>(null);
const fileInputRef = useRef<HTMLInputElement>(null);
const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
const waitingForUploadRef = useRef(false);

const imageUploadState = useSelector((state: RootState) => {
  const rootState = state as any;

  return (
    rootState.mediaLibrary ||
    rootState.imageUpload ||
    rootState.uploadImage ||
    rootState.media ||
    {}
  );
});

const uploading = Boolean(
  imageUploadState.uploading ?? imageUploadState.loading,
);

const uploadedImageUrl =
  imageUploadState.uploadedUrls?.[0] ??
  imageUploadState.uploadedImage?.url ??
  imageUploadState.uploadedImage?.imageUrl ??
  imageUploadState.uploadedImageUrl ??
  imageUploadState.imageUrl ??
  imageUploadState.data?.url ??
  imageUploadState.data?.imageUrl ??
  "";

const uploadError = imageUploadState.error?.message ?? imageUploadState.error ?? "";

const [formData, setFormData] = useState({
  title: initialData?.title || "",
  offerInfo: initialData?.offerDetails || "",
  tagLine: initialData?.tagLine || "",
  validFrom: initialData?.validFrom || "",
  validTo: initialData?.validTo || "",
  contactLocation: "",
  contactPhone: initialData?.contactInfo || "",
  contactEmail: "",
  Instructions: initialData?.Instructions || "",
  refImage: initialData?.refImage || ([] as string[]),
  themeColors: [initialData?.themeColour || "#EE4423"] as string[],
  category: initialData?.category || "",
  flyerImage: initialData?.flyerImage || "",
});

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      category: postYourOwn ? "post_your_own" : "",
    }));
  }, [postYourOwn]);

  const inputClassName =
    "w-full border-0 border-b border-gray-700 bg-transparent px-0 py-3 text-base text-white placeholder-gray-500 transition-colors focus:border-brand-primary focus:outline-none";
  const selectClassName = "h-7 w-[130px] cursor-pointer appearance-none rounded-sm border border-gray-700 bg-[#2a2a2a] px-3 pr-8 text-xs text-gray-300 outline-none transition-all focus:ring-1 focus:ring-gray-400";

  useEffect(() => { const savedInstructions = initialData?.Instructions || "";

if (!savedInstructions) { return;}

const lines = savedInstructions
  .split("\n")
  .map((line) => line.trim())
  .filter(Boolean);

let cleanInstructions: string[] = [];

let toneMood = "",
  targetAudience = "",
  quickStyle = "",
  savedAspectRatio = "",
  savedCustomWidth = "",
  savedCustomHeight = "",
  savedIsCustomSize = false;

lines.forEach((line) => {
  if (line.startsWith("Tone/Mood - ")) {
    toneMood = line.replace("Tone/Mood - ", "").trim();
    return;
  }

  if (line.startsWith("Audience - ")) {
    targetAudience = line.replace("Audience - ", "").trim();
    return;
  }

  if (line.startsWith("Quick Style - ")) {
    quickStyle = line.replace("Quick Style - ", "").trim();
    return;
  }

  if (line.startsWith("Aspect Ratio - ")) {
    const customDesign = line.replace("Aspect Ratio - ", "").trim();

  const customSizeMatch = customDesign.match(/^(\d+)\s*x\s*(\d+)\s*px$/i);

    if (customSizeMatch) {
      savedIsCustomSize = true;
      savedCustomWidth = customSizeMatch[1];
      savedCustomHeight = customSizeMatch[2];
    } else {
      savedAspectRatio = customDesign;
    }

  return;
}

cleanInstructions.push(line);
});

  setSelectedToneMood(toneMood);
  setSelectedTargetAudience(targetAudience);
  setSelectedshortCode(quickStyle);
  setSelectedAspectRatio(savedAspectRatio);
  setIsCustomSize(savedIsCustomSize);

if (savedCustomWidth) {
  setCustomWidth(savedCustomWidth);
}

if (savedCustomHeight) {
  setCustomHeight(savedCustomHeight);
}

setFormData((prev) => ({
  ...prev,
  Instructions: cleanInstructions.join("\n"),
}));
}, [initialData?.Instructions]);

useEffect(() => {const handleClickOutside = (event: MouseEvent) => {
  if (
    colorPickerRef.current &&
    !colorPickerRef.current.contains(event.target as Node)
  ) {
    setShowColorPicker(false);
  }
};

if (showColorPicker) {
  document.addEventListener("mousedown", handleClickOutside);
}

return () => {
  document.removeEventListener("mousedown", handleClickOutside);
};
}, [showColorPicker]);

useEffect(() => { const handleClickOutside = (event: MouseEvent) => {
  if (
    aspectRatioPickerRef.current &&
    !aspectRatioPickerRef.current.contains(event.target as Node)
  ) {
    setShowAspectRatioPicker(false);
  }
};

if (showAspectRatioPicker) {
  document.addEventListener("mousedown", handleClickOutside);
}

return () => {
  document.removeEventListener("mousedown", handleClickOutside);
};
}, [showAspectRatioPicker]);

useEffect(() => {
  if (restaurantId && menuItems.length === 0) {
    dispatch(
      fetchMenuItemsRequest({
        restaurantId,
      }),
    );
  }
}, [restaurantId, dispatch, menuItems.length]);

  useEffect(() => {
    if (
      initialData?.menuItemNames &&
      initialData.menuItemNames.length > 0 &&
      menuItems.length > 0 &&
      selectedMenuItems.length === 0
    ) {
      const ids = initialData.menuItemNames
        .map(
          (name) => menuItems.find((menuItem) => menuItem.name === name)?.pk_id,
        )
        .filter(Boolean) as string[];

      if (ids.length > 0) {
        setSelectedMenuItems(ids);
      }
    }
  }, [menuItems, initialData?.menuItemNames]);

  const getTodayDate = () => {
    const today = new Date();

    return today.toISOString().split("T")[0];
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (postYourOwn) {
      const flyerImage = formData.flyerImage;

      if (!flyerImage || pendingImages.length > 0 || uploading) {
        return;
      }

      onSubmit({
        ...formData,
        Instructions: "",
        category: "post_your_own",
        flyerImage,
      });

      return;
    }

    const selectedMenuItemObjs = menuItems
      .filter((item) => item.pk_id && selectedMenuItems.includes(item.pk_id))
      .map((item) => ({
        items: item.name,
      }));

    const instructionParts: string[] = [];

    if (formData.Instructions.trim()) {
      instructionParts.push(formData.Instructions.trim());
    }

    if (selectedToneMood) {
      instructionParts.push(`Tone/Mood - ${selectedToneMood}`);
    }

    if (selectedTargetAudience) {
      instructionParts.push(`Audience - ${selectedTargetAudience}`);
    }

    if (selectedshortCode) {
      instructionParts.push(`Quick Style - ${selectedshortCode}`);
    }

    if (isCustomSize && customWidth && customHeight) {
      instructionParts.push(
        `Aspect Ratio - ${customWidth} x ${customHeight} px`,
      );
    } else if (selectedAspectRatio) {
      instructionParts.push(`Aspect Ratio - ${selectedAspectRatio}`);
    }

    const finalInstructions = instructionParts.join("\n");
    onSubmit({
      ...formData,
      Instructions: finalInstructions,
      menuItems: selectedMenuItemObjs,
    });
  };

  const handleSelectMedia = useCallback(
    (url: string, type: "upload" | "pexels" | "template") => {
      setFormData((prev) => ({
        ...prev,
        refImage: [...prev.refImage, url],
      }));

      setShowMediaLibrary(false);
    },
    [],
  );

  const addPendingImage = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setPendingImages([
        {
          id: `${Date.now()}-${Math.random()}`,
          file,
          preview: reader.result as string,
          type: "local",
        },
      ]);

      // Post Your Own uses flyerImage only and never changes refImage.
      setFormData((prev) => ({
        ...prev,
        flyerImage: "",
      }));
    };

    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      addPendingImage(file);
    }
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    const file = Array.from(e.dataTransfer.files).find((item) =>
      item.type.startsWith("image/"),
    );

    if (file) {
      addPendingImage(file);
    }
  };

  const handleUploadNow = () => {
    if (pendingImages.length === 0 || uploading) return;

    const firstImage = pendingImages[0];
    waitingForUploadRef.current = true;

    if (firstImage.file) {
      dispatch(
        uploadImageRequest({
          file: firstImage.file,
        }),
      );
    } else if (firstImage.pexelsUrl) {
      dispatch(
        uploadImageRequest({
          pexelsUrl: firstImage.pexelsUrl,
        }),
      );
    } else if (firstImage.templateUrl) {
      dispatch(
        uploadImageRequest({
          templateUrl: firstImage.templateUrl,
        }),
      );
    }
  };

  useEffect(() => {
    if (
      !waitingForUploadRef.current ||
      !imageUploadState.success ||
      !uploadedImageUrl
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      category: "post_your_own",
      flyerImage: uploadedImageUrl,
    }));

    setPendingImages([]);
    waitingForUploadRef.current = false;
  }, [imageUploadState.success, uploadedImageUrl]);

  useEffect(() => {
    if (!waitingForUploadRef.current || uploading || !uploadError) {
      return;
    }

    // Keep the local preview visible so the user can retry the upload.
    waitingForUploadRef.current = false;
  }, [uploadError, uploading]);

  const clearPostYourOwnImage = useCallback(() => {
    waitingForUploadRef.current = false;
    setPendingImages([]);

    setFormData((prev) => ({
      ...prev,
      flyerImage: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const removeRefImage = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      refImage: prev.refImage.filter((_, i) => i !== index),
    }));
  }, []);

  const isFormValid = Boolean(
    formData.title.trim() && formData.validFrom && formData.validTo,
  );

  const isPostYourOwnFormValid = Boolean(
    isFormValid &&
    formData.category === "post_your_own" &&
    formData.flyerImage &&
    pendingImages.length === 0 &&
    !uploading,
  );

  return (
    <>
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="w-full">
          <input
            type="text"
            value={formData.title}
            id="campaignTitle"
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            placeholder="Campaign Title"
            className={inputClassName}
            required
          />
        </div>

        <div className="flex gap-6">
          <div className="w-1/2">
            <input
              type="text"
              value={formData.tagLine}
              id="Tagline"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  tagLine: e.target.value,
                }))
              }
              placeholder="Tagline"
              className={inputClassName}
            />
          </div>

          <div className="menu-selector-bottom-border w-1/2">
            <MenuSelector
              items={menuItems}
              selectedItems={selectedMenuItems}
              onSelectionChange={setSelectedMenuItems}
              loading={menuLoading}
            />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="w-3/4">
            <input
              type="text"
              value={formData.contactPhone}
              id="contactPhone"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  contactPhone: e.target.value,
                }))
              }
              placeholder="Phone / email / web url / location goes here"
              className={inputClassName}
            />
          </div>

          <div ref={colorPickerRef} className="flex w-1/4 items-center gap-2">
            <label className="relative flex-shrink-0 cursor-pointer">
              <div
                className="h-6 w-6 rounded border-0 border-b border-gray-700"
                style={{
                  backgroundColor: selectedColor || "transparent",
                }}
              />

              <input
                type="color"
                value={selectedColor || "#000000"}
                id="themeColor"
                onChange={(e) => {
                  const newColor = e.target.value;
                  setSelectedColor(newColor);
                  setFormData((prev) => ({
                    ...prev,
                    themeColors: [newColor],
                  }));
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </label>

            <input
              type="text"
              value={selectedColor}
              id="themeColors"
              onChange={(e) => {
                const color = e.target.value;

                if (/^#[0-9A-Fa-f]{0,6}$/.test(color)) {
                  setSelectedColor(color);

                  if (color.length === 7) {
                    setFormData((prev) => ({
                      ...prev,
                      themeColors: [color],
                    }));
                  }
                }
              }}
              placeholder="#000000"
              className="min-w-0 flex-1 border-0 border-b border-gray-700 bg-transparent px-0 py-3 text-base text-white transition-colors focus:border-brand-primary focus:outline-none"
            />

            {selectedColor && (
              <button
                type="button"
                onClick={() => {
                  setSelectedColor("");

                  setFormData((prev) => ({
                    ...prev,
                    themeColors: [],
                  }));
                }}
                className="flex-shrink-0 cursor-pointer text-gray-400 transition-colors hover:text-white"
                aria-label="Clear theme color"
              >
                <MdClose className="text-lg" />
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-6">
          <div className="w-1/2">
            <input
              type="text"
              id="offerInfo"
              value={formData.offerInfo}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  offerInfo: e.target.value,
                }))
              }
              placeholder="Offer Info"
              className={inputClassName}
            />
          </div>

          <div className="relative w-1/4">
            {!formData.validFrom && (
              <span className="pointer-events-none absolute left-0 top-3 z-10 text-base text-gray-500">
                From
              </span>
            )}

            <input
              type="date"
              id="validFrom"
              value={formData.validFrom}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  validFrom: e.target.value,
                }))
              }
              onClick={(e) => {
                const target = e.currentTarget;
                if (target.showPicker) {
                  target.showPicker();
                }
              }}
              min={getTodayDate()}
              className={`w-full cursor-pointer border-0 border-b border-gray-700 bg-transparent px-0 py-3 text-base text-white transition-colors focus:border-brand-primary focus:outline-none ${
                formData.validFrom
                  ? "[&::-webkit-datetime-edit-fields-wrapper]:opacity-100 [&::-webkit-datetime-edit]:opacity-100"
                  : "[&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit]:opacity-0"
              } [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100`}
              required
            />
          </div>

          <div className="relative w-1/4">
            {!formData.validTo && (
              <span className="pointer-events-none absolute left-0 top-3 z-10 text-base text-gray-500">
                {" "}
                To{" "}
              </span>
            )}

            <input
              type="date"
              value={formData.validTo}
              id="validTo"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  validTo: e.target.value,
                }))
              }
              onClick={(e) => {
                const target = e.currentTarget;

                if (target.showPicker) {
                  target.showPicker();
                }
              }}
              min={formData.validFrom || getTodayDate()}
              className={`w-full cursor-pointer border-0 border-b border-gray-700 bg-transparent px-0 py-3 text-base text-white transition-colors focus:border-brand-primary focus:outline-none ${
                formData.validTo
                  ? "[&::-webkit-datetime-edit-fields-wrapper]:opacity-100 [&::-webkit-datetime-edit]:opacity-100"
                  : "[&::-webkit-datetime-edit-fields-wrapper]:opacity-0 [&::-webkit-datetime-edit]:opacity-0"
              } [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100`}
              required
            />
          </div>
        </div>
        {!postYourOwn && (
          <div className="relative">
            <textarea
              id="instructions"
              value={formData.Instructions}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  Instructions: e.target.value,
                }))
              }
              placeholder="Example: Create a Halloween campaign running from 25–31 October with 25% off desserts. Use a spooky yet fun theme with black and orange colours, pumpkins, playful typography, and eye-catching social media graphics that encourage customers to visit before the offer ends."
              rows={5}
              className="w-full max-h-64 min-h-[140px] resize-none overflow-y-auto rounded-xl border border-transparent bg-[#2a2a2a] px-6 pb-16 pt-4 text-base text-white placeholder-gray-500 transition-all focus:outline-none"
              style={{
                background:
                  "linear-gradient(#2a2a2a, #2a2a2a) padding-box, linear-gradient(90deg, rgba(238, 69, 35, 0.63), #8E44AD, #3498DB, #2a2a2a) border-box",
                backgroundSize: "100% 100%, 200% 200%",
                animation: "borderAnimation 3s linear infinite",
              }}
            />

            <style>{`
          @keyframes borderAnimation {
            0% {
              background-position: 0% 50%, 0% 50%;
            }

            50% {
              background-position: 0% 50%, 100% 50%;
            }

            100% {
              background-position: 0% 50%, 200% 50%;
            }
          }
        `}</style>

            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20 rounded-b-xl bg-gradient-to-t from-[#2a2a2a] via-[#2a2a2a] to-transparent" />

            <div className="absolute bottom-4 left-6 right-6 z-20 flex items-center justify-between gap-4 rounded-lg bg-[#2a2a2a] py-1">
              <div className="flex min-w-0 flex-1 items-center gap-6">
                {/* ADD IMAGE */}

                <div className="flex flex-shrink-0 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowMediaLibrary(true)}
                    className="flex flex-shrink-0 cursor-pointer items-center justify-center text-sm text-gray-400 transition-colors hover:text-white"
                    aria-label="Add reference image"
                  >
                    <MdAdd className="text-xl" />
                  </button>

                  {formData.refImage.map((image, index) => (
                    <div
                      key={index}
                      className="group relative h-9 w-9 flex-shrink-0"
                    >
                      <img
                        src={image}
                        alt={`Reference ${index + 1}`}
                        className="h-9 w-9 rounded-lg border border-gray-600 object-cover"
                      />

                      <button
                        type="button"
                        onClick={() => removeRefImage(index)}
                        className="absolute right-0 top-0 z-10 flex h-4 w-4 -translate-y-1/2 translate-x-1/2 cursor-pointer items-center justify-center rounded-full bg-red-500 shadow-md transition-colors hover:bg-red-600"
                        aria-label={`Remove reference ${index + 1}`}
                      >
                        <MdClose size={10} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex flex-shrink-0 items-center gap-3">
                  <div className="relative">
                    <select
                      id="toneMood"
                      value={selectedToneMood}
                      onChange={(e) => setSelectedToneMood(e.target.value)}
                      className={selectClassName}
                    >
                      <option value="">Theme</option>

                      {toneMoodOptions.map((option) => (
                        <option key={option} value={option}>
                          {" "}
                          {option}{" "}
                        </option>
                      ))}
                    </select>

                    <svg
                      className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-3">
                  <div className="relative">
                    <select
                      id="audience"
                      value={selectedTargetAudience}
                      onChange={(e) =>
                        setSelectedTargetAudience(e.target.value)
                      }
                      className={selectClassName}
                    >
                      <option value="">Audience</option>

                      {targetAudienceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>

                    <svg
                      className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                {/* QUICK STYLE */}

                <div className="flex flex-shrink-0 items-center gap-3">
                  <div className="relative">
                    <select
                      id="shortCode"
                      value={selectedshortCode}
                      onChange={(e) => setSelectedshortCode(e.target.value)}
                      className={selectClassName}
                    >
                      <option value="">Quick Style</option>

                      {shortCodeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>

                    <svg
                      className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>

                <div
                  ref={aspectRatioPickerRef}
                  className="relative flex flex-shrink-0 items-center gap-3"
                >
                  <div className="relative">
                    <button
                      type="button"
                      id="AspectRatio"
                      onClick={() =>
                        setShowAspectRatioPicker((isOpen) => !isOpen)
                      }
                      className={`${selectClassName} flex items-center justify-between`}
                    >
                      <span>
                        {isCustomSize
                          ? `${customWidth} × ${customHeight}`
                          : selectedAspectRatio || "AspectRatio"}
                      </span>

                      <svg
                        className="h-4 w-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>

                  {showAspectRatioPicker && (
                    <div className="absolute bottom-full right-0 z-50 mb-3 w-[360px] rounded-xl border border-gray-700 bg-[#1f1f1f] p-1 shadow-2xl">
                      {isCustomSize && (
                        <div className="mb-3 flex items-center gap-3 rounded-lg bg-[#171717] p-1">
                          <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-600 bg-[#222] px-3 py-2">
                            <svg
                              className="h-5 w-5 flex-shrink-0 text-gray-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M4 12h16M7 9l-3 3 3 3m10-6 3 3-3 3"
                              />
                            </svg>

                            <input
                              type="number"
                              id="customWidth"
                              min="1"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(e.target.value)}
                              className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none"
                              aria-label="Custom width in pixels"
                            />

                            <span className="text-sm text-gray-400"> px </span>
                          </label>

                          <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-gray-600 bg-[#222] px-3 py-2">
                            <svg
                              className="h-5 w-5 flex-shrink-0 text-gray-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M12 4v16M9 7l3-3 3 3m-6 10 3 3 3-3"
                              />
                            </svg>

                            <input
                              type="number"
                              min="1"
                              id="customHeight"
                              value={customHeight}
                              onChange={(e) => setCustomHeight(e.target.value)}
                              className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none"
                              aria-label="Custom height in pixels"
                            />
                            <span className="text-sm text-gray-400"> px </span>
                          </label>
                        </div>
                      )}

                      <div className="grid grid-cols-4 gap-1.5">
                        <button
                          type="button"
                          id="customHeight"
                          onClick={() => {
                            setIsCustomSize(true);
                            setSelectedAspectRatio("");
                          }}
                          className={`flex  w-full flex-col items-center justify-center gap-1.5 rounded text-[10px] text-gray-200 transition-colors ${
                            isCustomSize
                              ? "bg-[#3a3a3a]"
                              : "bg-[#292929] hover:bg-[#343434]"
                          }`}
                        >
                          <svg
                            className="h-4 w-4 fill-current"
                            viewBox="0 0 24 24"
                          >
                            <path d="M4 4h6v2H6v4H4V4Zm10 0h6v6h-2V6h-4V4ZM4 14h2v4h4v2H4v-6Zm14 0h2v6h-6v-2h4v-4Z" />
                          </svg>
                          Custom
                        </button>

                        {aspectRatio.map((option) => (
                          <button
                            type="button"
                            key={option.value}
                            onClick={() => {
                              setSelectedAspectRatio(option.value);
                              setIsCustomSize(false);
                              setShowAspectRatioPicker(false);
                            }}
                            className={`flex pt-2 pb-2 cursor-pointer w-full flex-col items-center justify-center gap-1.5 rounded text-[10px] text-gray-200 transition-colors ${
                              !isCustomSize &&
                              selectedAspectRatio === option.value
                                ? "bg-[#3a3a3a]"
                                : "bg-[#292929] hover:bg-[#343434]"
                            }`}
                          >
                            <svg
                              className="h-5 w-5 fill-current"
                              viewBox={option.icon.viewBox}
                              aria-hidden="true"
                            >
                              {" "}
                              <path d={option.icon.path} />
                            </svg>
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                  title={submitLabel}
                  className={`rounded-full p-2 transition-colors ${
                    isSubmitting
                      ? "cursor-not-allowed bg-brand-primary"
                      : isFormValid
                        ? "cursor-pointer bg-brand-primary hover:opacity-90"
                        : "cursor-not-allowed bg-gray-800 hover:bg-gray-700"
                  }`}
                >
                  {isSubmitting ? (
                    <svg
                      className="h-5 w-5 animate-spin text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {postYourOwn && (
          <div className="w-full pt-2">
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="relative min-h-[100px] overflow-hidden rounded-2xl border-2 border-dashed border-gray-700 bg-surface-dark transition-all hover:border-brand-primary hover:bg-brand-primary/5"
            >
              {pendingImages.length === 0 && !formData.flyerImage ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex min-h-[100px] w-full cursor-pointer flex-col items-center justify-center gap-4 p-6"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-800">
                    <MdCloudUpload size={24} className="text-gray-400" />
                  </div>

                  <div className="text-center">
                    <p className="mb-2 text-xs font-medium text-white">
                      Drop an image here or click to browse
                    </p>

                    <p className="text-[10px] text-gray-400">
                      JPG, PNG, GIF or WebP. Only one image is allowed.
                    </p>
                  </div>
                </button>
              ) : (
                <div className="relative flex min-h-[260px] items-center justify-center p-4">
                  <img
                    src={formData.flyerImage || pendingImages[0]?.preview}
                    alt={
                      formData.flyerImage
                        ? "Uploaded campaign image"
                        : "Selected campaign image preview"
                    }
                    className="max-h-[360px] w-full rounded-xl object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearPostYourOwnImage}
                    disabled={uploading}
                    className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/75 text-white shadow-lg transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    <MdClose size={18} />
                  </button>

                  {pendingImages.length > 0 && (
                    <button
                      type="button"
                      onClick={handleUploadNow}
                      disabled={uploading}
                      className="absolute bottom-4 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary text-white shadow-lg transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Upload image"
                      title={uploading ? "Uploading image" : "Upload image"}
                    >
                      {uploading ? (
                        <svg
                          className="h-5 w-5 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4Z"
                          />
                        </svg>
                      ) : (
                        <MdCloudUpload size={24} />
                      )}
                    </button>
                  )}

                  {formData.flyerImage && (
                    <div className="absolute bottom-4 left-4 rounded-full bg-green-600/90 px-3 py-1 text-xs font-medium text-white shadow-md">
                      Upload successful
                    </div>
                  )}
                </div>
              )}
            </div>

            {uploadError && pendingImages.length > 0 && (
              <p className="mt-2 text-xs text-red-400">{String(uploadError)}</p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting || !isPostYourOwnFormValid}
                title={submitLabel}
                className={`rounded-full p-2 transition-colors ${
                  isSubmitting
                    ? "cursor-not-allowed bg-brand-primary"
                    : isPostYourOwnFormValid
                      ? "cursor-pointer bg-brand-primary hover:opacity-90"
                      : "cursor-not-allowed bg-gray-800"
                }`}
              >
                {isSubmitting ? (
                  <svg
                    className="h-5 w-5 animate-spin text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4Z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 10l7-7m0 0l7 7m-7-7v18"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}
      </motion.form>

      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelectMedia={handleSelectMedia}
        selectedImages={formData.refImage}
        onRemoveImage={removeRefImage}
        initialTab="upload"
      />
    </>
  );
};

export default CreateOwnForm;
